/** What a trip room asks of the socket. */
export type TripRoomEvent = 'join_trip' | 'leave_trip'

export interface TripRooms {
	/** Holds the trip's room; the socket joins it when the first holder arrives. */
	join: (tripId: string) => void
	/** Lets go of the room; the socket leaves it when the last holder goes. */
	leave: (tripId: string) => void
	/** Joins every held room again, as a reconnect needs: the server's new session has none. */
	rejoinAll: () => void
}

/**
 * The trip rooms this browser holds, counted per surface. Socket.IO room membership is
 * per socket, not per caller, so without the count a surface inside another — a
 * Discussion in an itinerary modal — would take the itinerary's room with it on close.
 * `emit` only runs while `isConnected()`; a room held while offline is joined on reconnect.
 */
export const createTripRooms = (
	emit: (event: TripRoomEvent, tripId: string) => void,
	isConnected: () => boolean,
): TripRooms => {
	const holders = new Map<string, number>()
	return {
		join: tripId => {
			const count = holders.get(tripId) ?? 0
			holders.set(tripId, count + 1)
			if (count === 0 && isConnected()) emit('join_trip', tripId)
		},
		leave: tripId => {
			const count = holders.get(tripId) ?? 0
			if (count > 1) {
				holders.set(tripId, count - 1)
				return
			}
			holders.delete(tripId)
			if (count === 1 && isConnected()) emit('leave_trip', tripId)
		},
		rejoinAll: () => {
			if (!isConnected()) return
			for (const tripId of holders.keys()) emit('join_trip', tripId)
		},
	}
}
